
import { db } from '@/lib/firebase';
import { normalizeGenre, normalizeGenres } from '@/lib/genres';
import { stripTrackNumber } from '@/lib/id3-reader';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import type { MediaItem, Work, Release, ReleaseType, EntityRef, ActingAs } from '@/types/media';
import { propagateReleaseCoverById } from './cover-index';

/**
 * Recursively remove undefined values from an object before writing to Firestore.
 * Firestore's setDoc/updateDoc throw "Unsupported field value: undefined" if any
 * nested field is undefined — this strips them (undefined → omitted, not null).
 */
function sanitizeForFirestore<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj, (_key, value) => (value === undefined ? null : value)));
}

// Types for creating new works
export interface CreateWorkInput {
  title: string;
  /** AI disclosure / provenance — the "Music Ingredients Label". */
  aiDisclosure?: import('@/data/ai-disclosure').AIDisclosure;
  /** "How it's made" provenance label (live → machine → partly/fully AI). */
  aiLevel?: import('@/lib/ai-authorship').AiLevel;
  /** Rough AI % (0–100) for the provenance label. */
  aiPercent?: number;
  /** Alternative title / AKA — used by copyright societies (PROs) for tracking */
  alternativeTitle?: string;
  artist?: string;
  /** Firestore document ID of the linked artist profile */
  artistId?: string;
  description?: string;
  type: string;
  genre?: string;
  genres?: string[];
  tags?: string[];
  coverArtUrl?: string;
  isrc?: string;
  iswc?: string;
  privacy?: string;
  /** Audio-reactive cover-art effect that animates while this track plays. */
  artworkFx?: string;
  artworkFxIntensity?: number;
  artworkFxColor?: string;
  artworkTitleColor?: string;
  copyrightHolder?: string;
  copyrightYear?: number;
  pro?: string;
  publisher?: string;
  contributors?: {
    name: string;
    /** Linked BB user id — set when contributor is selected from the users search */
    userId?: string;
    role: string;
    roles?: string[];
    instrument?: string;
    split?: number;
  }[];
  lyrics?: string;
  explicit?: boolean;
  allowDownloads?: boolean;
  allowComments?: boolean;
  monetization?: boolean;
  publishDate?: Date;
  status?: string;
  /** URL to the uploaded audio file in Firebase Storage */
  audioUrl?: string;
  /** Actual audio/video file format e.g. "mp3", "wav", "aiff", "mp4" */
  fileFormat?: string;
  /** Download type: 'free' | 'pwyw' (name your price) | 'paid' (fixed price) */
  downloadType?: 'free' | 'pwyw' | 'paid';
  /** Minimum price in cents (0 for PWYW allowing free) */
  downloadMinPrice?: number;
  /** Currency code: 'eur', 'usd', 'gbp', 'cad', 'sek' */
  downloadCurrency?: string;
  /** DAW/sampler project file sold as its own PWYW product — e.g. a Teenage
   *  Engineering EP-133 K.O. II / EP-1320 session (Storage URL). Legacy
   *  single-file fields; kept in sync with projectFiles[0] for old readers. */
  projectFileUrl?: string;
  /** Original filename of the project file (served as the download name). */
  projectFileName?: string;
  /** ALL project files for the setup — one track can span multiple machines
   *  (e.g. beats pack + vox pack for 2× EP-40). Buyers get every file
   *  (zipped together when more than one). */
  projectFiles?: { url: string; name: string }[];
  /** Human label for the setup: "Teenage Engineering EP-X",
   *  "2× EP-40 (beats + vox)", "EP-40 + EP-136 sidekick", "Ableton Live"… */
  projectFileFormat?: string;
  /** Pay-what-you-feel floor for the project file, in cents (0 = no floor). */
  projectFileMinPrice?: number;
  /** Beats per minute, detected by AI */
  bpm?: number;
  /** Musical key, e.g. "C Major", "A Minor" */
  key?: string;
  /** Album / release title this track belongs to (denormalised for quick lookup) */
  album?: string;
  /** Additional artwork images (first = thumbnail / coverArtUrl) */
  images?: string[];
  /** Track length in seconds (auto-detected from audio/video file, or entered manually) */
  durationSeconds?: number;
  /** Original hi-res file (WAV/AIF) for purchase and sync licensing; streaming uses web-optimised version */
  hiResUrl?: string;
  /** URL to a video file in Firebase Storage (mp4, mov, webm etc.) */
  videoUrl?: string;
  /** Number of times this work has been downloaded */
  downloadCount?: number;
  /** Featured / guest artists on this track, e.g. ["Artist B", "Artist C"] */
  featuredArtists?: string[];
}

// Types for creating releases
export interface CreateReleaseInput {
  title: string;
  releaseType: ReleaseType;
  artist: string;
  coverArtUrl?: string;
  releaseDate?: Date;
  genre?: string;
  explicit?: boolean;
  copyrightHolder?: string;
  copyrightYear?: number;
  upc?: string;
  catalogNumber?: string;
}

/**
 * Fetches all items from the "works" collection in Firestore.
 * This service will need to be expanded to fetch from releases, videos, etc.
 * @returns A promise that resolves to an array of media items.
 */
/**
 * Fetch all media items regardless of owner — for Super Admin / Admin.
 */
export async function getMediaItems(): Promise<MediaItem[]> {
  return getMediaItemsForUser(null);
}

/**
 * Fetch media items.
 * - ownerUid = null  → return ALL works (super admin / admin)
 * - ownerUid = string → return only works owned by that user
 */
export async function getMediaItemsForUser(ownerUid: string | null): Promise<MediaItem[]> {
  try {
    const mediaCollection = collection(db, 'works');
    const mediaQuery = ownerUid
      ? query(mediaCollection, where('owner.userId', '==', ownerUid), orderBy('title', 'asc'))
      : query(mediaCollection, orderBy('title', 'asc'));

    // Fetch works + releases in parallel — releases needed for cover-art fallback
    const [querySnapshot, releasesSnap] = await Promise.all([
      getDocs(mediaQuery),
      getDocs(collection(db, 'releases')),
    ]);

    // Build workId → release coverArtUrl reverse map
    const coverMap = new Map<string, string>();
    releasesSnap.docs.forEach(rd => {
      const rd2 = rd.data();
      const cover = rd2.coverArtUrl || rd2.imageUrl || '';
      if (!cover) return;
      // Map release id itself
      if (!coverMap.has(rd.id)) coverMap.set(rd.id, cover);
      // Map each linked track workId
      if (Array.isArray(rd2.tracks)) {
        rd2.tracks.forEach((t: any) => {
          const wid = t.workId || t.id;
          if (wid && !coverMap.has(wid)) coverMap.set(wid, cover);
        });
      }
    });

    const items = querySnapshot.docs
      // Drop tracks with no playable audio source. Per Gordon: "you still
      // showing tracks that have no audio files. hide all tracks without
      // audio files." Admins still see them via the Super Admin → Media
      // panel, which uses getAllMedia() directly. Tracks with NO audio
      // URL across any of the legacy field names get filtered here at
      // the source so every consumer of getMediaItems (home, charts,
      // profile, search) is silent on them automatically.
      .filter(d => {
        const data = d.data() as any;
        // Video works keep their existing visibility rules — only filter
        // pure audio works that have no audio file.
        if (data.type === 'video' || data.videoUrl || data.youtubeId) return true;
        const audio = data.audioUrl || data.mp3Url || data.hiResUrl || data.sourceUrl || data.fileUrl || '';
        return !!audio;
      })
      .map(d => {
        const data = d.data();
        // 3-tier cover fallback: work's own art → release art → nothing
        const coverArtUrl =
          data.coverArtUrl ||
          data.imageUrl ||
          coverMap.get(d.id) ||
          '';
        return {
          ...data,
          // Strip any leading track numbers (e.g. "01 Song", "1. Song") from display title
          title: data.title ? stripTrackNumber(data.title) : data.title,
          workId: d.id,
          type: data.type || 'Work',
          status: data.status || 'Draft',
          releaseDate: data.releaseDate || data.createdAt,
          coverArtUrl,
          imageUrl: coverArtUrl, // keep imageUrl alias in sync
        } as MediaItem;
      });

    return items;
  } catch (error) {
    console.error("Error fetching media items from Firestore:", error);
    return [];
  }
}

/**
 * Creates a new work/media item in Firestore
 */
export async function createWork(
  userId: string,
  input: CreateWorkInput
): Promise<string> {
  try {
    const workId = `work_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const workRef = doc(db, 'works', workId);

    // Build writers array from contributors who are writers/composers
    const writers = input.contributors
      ?.filter(c => ['writer', 'composer', 'songwriter', 'lyricist'].includes(c.role.toLowerCase()))
      .map(c => ({
        entityRef: {
          docId: '',
          collection: 'users',
          name: c.name,
          path: '',
        } as EntityRef,
        split: 0,
      })) || [];

    const workData: Record<string, unknown> = {
      workId,
      title: stripTrackNumber(input.title),
      // Write the canonical denormalised string under BOTH `artist` and
      // `artistName` — different parts of the codebase historically read
      // from one or the other. Bulk-upload used to set `artist` only and the
      // music page only looks at `artist.name` / `artistName`, which is why
      // a freshly uploaded track displayed "Unknown Artist".
      artist: input.artist || '',
      artistName: input.artist || '',
      artistId: input.artistId || '',
      description: input.description || '',
      type: input.type,
      genre: normalizeGenre(input.genre || (input.genres?.[0] ?? '')),
      genres: normalizeGenres(input.genres ?? (input.genre ? [input.genre] : [])),
      tags: input.tags || [],
      coverArtUrl: input.coverArtUrl || '',
      audioUrl: input.audioUrl || '',
      mp3Url: input.audioUrl || '',  // backward compat alias
      videoUrl: input.videoUrl || '',
      fileFormat: input.fileFormat || '',
      hiResUrl: input.hiResUrl || '',  // WAV lossless copy for download
      isrc: input.isrc || '',
      iswc: input.iswc || '',
      privacy: input.privacy || 'public',
      copyrightHolder: input.copyrightHolder || '',
      copyrightYear: input.copyrightYear || new Date().getFullYear(),
      pro: input.pro || '',
      publisher: input.publisher || '',
      contributors: input.contributors || [],
      featuredArtists: input.featuredArtists || [],
      // Flat email index — lets Firestore array-contains find this work by contributor email
      contributorEmails: (input.contributors || [])
        .map(c => (c as any).email)
        .filter(Boolean) as string[],
      // Flat userId index — lets a user's profile find works they're credited on
      // (Firestore can't query the contributors[] objects by sub-field directly).
      contributorUserIds: (input.contributors || [])
        .map(c => (c as any).userId)
        .filter(Boolean) as string[],
      lyrics: input.lyrics || '',
      explicit: input.explicit || false,
      allowDownloads: input.allowDownloads ?? true,
      // Default to 'paid' (not 'pwyw' with $0) — the previous pwyw+0 default
      // combined with the server's "pwyw + 0 = free" bypass meant every
      // upload was implicitly downloadable for free by any signed-in user.
      // Artists who want free downloads now have to opt in explicitly via
      // the Pricing form; everything else routes through Stripe. paywall
      // stays intact by default.
      // Default to pay-what-you-want with a €1.00 floor (100 cents) so every new
      // upload is downloadable AND paywalled (routes through Stripe) by default.
      downloadType: input.downloadType || 'pwyw',
      downloadMinPrice: input.downloadMinPrice ?? 100,
      downloadCurrency: input.downloadCurrency || 'eur',
      downloadCount: input.downloadCount ?? 0,
      allowComments: input.allowComments ?? true,
      // Monetize by default (Gordon: "monetizing by default").
      monetization: input.monetization ?? true,
      status: input.status || 'Draft',
      album: input.album || '',
      ...(input.aiDisclosure ? { aiDisclosure: input.aiDisclosure } : {}),
      // "How it's made" provenance label (live → machine → partly/fully AI).
      ...(input.aiLevel ? { aiLevel: input.aiLevel } : {}),
      ...(typeof input.aiPercent === 'number' ? { aiPercent: input.aiPercent } : {}),
      writers,
      publishers: [],
      owner: {
        userId,
      } as ActingAs,
      source: 'platform',   // distinguishes genuine BB uploads from scraped/imported content
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      releaseDate: input.publishDate ? Timestamp.fromDate(input.publishDate) : serverTimestamp(),
    };

    // Preserve native Firestore sentinels; JSON serialization turns them into
    // {_methodName: 'serverTimestamp'}, which corrupted 218 existing upload dates.
    const { createdAt, updatedAt, releaseDate, ...plainData } = workData;
    await setDoc(workRef, { ...sanitizeForFirestore(plainData), createdAt, updatedAt, releaseDate });
    return workId;
  } catch (error) {
    console.error("Error creating work:", error);
    throw error;
  }
}

/**
 * Updates an existing work/media item
 */
export async function updateWork(
  workId: string,
  updates: Partial<CreateWorkInput>
): Promise<void> {
  try {
    const workRef = doc(db, 'works', workId);

    // IMPORTANT: extract publishDate before sanitizing — it becomes a Firestore
    // Timestamp which must NOT pass through sanitizeForFirestore (JSON.stringify
    // destroys Firestore sentinel objects like serverTimestamp() and Timestamp).
    const { publishDate, ...plainUpdates } = updates as Record<string, unknown>;

    // Strip track number prefix from title if being updated
    if (typeof plainUpdates.title === 'string' && plainUpdates.title) {
      plainUpdates.title = stripTrackNumber(plainUpdates.title);
    }

    // Sanitize only the plain JS data (converts undefined → null, etc.)
    const sanitized = sanitizeForFirestore(plainUpdates) as Record<string, unknown>;

    // Now add Firestore sentinel values AFTER sanitization
    const updateData: Record<string, unknown> = {
      ...sanitized,
      updatedAt: serverTimestamp(),  // sentinel — must not be JSON-serialized
    };

    // Convert publishDate → Firestore Timestamp after sanitization
    if (publishDate instanceof Date) {
      updateData.releaseDate = Timestamp.fromDate(publishDate);
    }

    // Keep the flat contributor indexes in sync when contributors change, so a
    // user's profile can find works they're credited on (array-contains query).
    if (Array.isArray((updates as any).contributors)) {
      const contribs = (updates as any).contributors as any[];
      updateData.contributorUserIds = contribs.map(c => c?.userId).filter(Boolean);
      updateData.contributorEmails = contribs.map(c => c?.email).filter(Boolean);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateDoc(workRef, updateData as any);

    // Favorites show a thumbnail SNAPSHOT taken at favorite-time — nothing
    // else re-fetches it, so the header would keep showing the old cover
    // until unfavorited/re-favorited. FavoritesBar listens for this and
    // live-refreshes instead (Gordon: "make sure... favorites in the header
    // automatically updates" when a thumbnail changes).
    if ('coverArtUrl' in plainUpdates || 'imageUrl' in plainUpdates) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bb:thumbnail-updated', { detail: { itemType: 'track', itemId: workId } }));
        }
      } catch { /* non-fatal */ }
    }

    // Denormalization sync: a release embeds a SNAPSHOT of each track's title in
    // its `tracks[]` array, and the media player plays from that snapshot. So a
    // rename must be pushed into the release too, else the player + release page
    // keep showing the OLD title. Same for the explicit flag.
    if (typeof plainUpdates.title === 'string' && plainUpdates.title) {
      try { await syncWorkFieldsToReleases(workId, { title: plainUpdates.title as string }); } catch { /* non-fatal */ }
    }
    if (typeof (plainUpdates as any).explicit === 'boolean') {
      try { await syncWorkFieldsToReleases(workId, { explicit: (plainUpdates as any).explicit as boolean }); } catch { /* non-fatal */ }
    }
  } catch (error) {
    console.error("Error updating work:", error);
    throw error;
  }
}

/**
 * Push a work's field(s) into the embedded `tracks[]` snapshot of any release
 * that contains it (found via the denormalized `releaseId`/`releaseIds`). Keeps
 * the release page + media player title/explicit in sync after a work edit.
 */
async function syncWorkFieldsToReleases(
  workId: string,
  patch: { title?: string; explicit?: boolean },
): Promise<void> {
  const workSnap = await getDoc(doc(db, 'works', workId));
  const wd = workSnap.data() as any;
  if (!wd) return;
  const releaseIds = new Set<string>();
  if (wd.releaseId) releaseIds.add(wd.releaseId);
  if (Array.isArray(wd.releaseIds)) wd.releaseIds.forEach((r: string) => r && releaseIds.add(r));

  for (const rid of releaseIds) {
    try {
      const relRef = doc(db, 'releases', rid);
      const relSnap = await getDoc(relRef);
      if (!relSnap.exists()) continue;
      const rd = relSnap.data() as any;
      if (!Array.isArray(rd.tracks)) continue;
      let changed = false;
      const tracks = rd.tracks.map((t: any) => {
        if (t?.workId === workId || t?.id === workId) {
          const next = { ...t };
          if (patch.title !== undefined && next.title !== patch.title) { next.title = patch.title; changed = true; }
          if (patch.explicit !== undefined && next.explicit !== patch.explicit) { next.explicit = patch.explicit; changed = true; }
          return next;
        }
        return t;
      });
      if (!changed) continue;
      const relUpdate: Record<string, unknown> = { tracks };
      // A release is explicit if any embedded track is explicit.
      if (patch.explicit !== undefined) {
        relUpdate.explicit = tracks.some((t: any) => t?.explicit === true);
      }
      await setDoc(relRef, relUpdate, { merge: true });
    } catch { /* per-release non-fatal */ }
  }
}

/**
 * Links or unlinks a work to an artist by writing artistId + artist name.
 * Pass empty strings to detach.
 */
export async function updateWorkArtist(
  workId: string,
  artistId: string,
  artistName: string
): Promise<boolean> {
  try {
    const workRef = doc(db, 'works', workId);
    await updateDoc(workRef, {
      artistId,
      artist: artistName,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error updating work artist:', error);
    return false;
  }
}

/**
 * Deletes a work/media item
 */
export async function deleteWork(workId: string): Promise<void> {
  try {
    const workRef = doc(db, 'works', workId);
    await deleteDoc(workRef);
  } catch (error) {
    console.error("Error deleting work:", error);
    throw error;
  }
}

/**
 * Gets a single work by ID
 */
export async function getWork(workId: string): Promise<MediaItem | null> {
  try {
    const workRef = doc(db, 'works', workId);
    const docSnap = await getDoc(workRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    let coverArtUrl = data.coverArtUrl || data.imageUrl || '';

    // Tier 2 fallback: if work has no cover, check the linked release
    if (!coverArtUrl && data.releaseId) {
      try {
        const relSnap = await getDoc(doc(db, 'releases', data.releaseId));
        if (relSnap.exists()) {
          const rd = relSnap.data();
          coverArtUrl = rd.coverArtUrl || rd.imageUrl || '';
        }
      } catch { /* non-fatal */ }
    }

    return {
      ...data,
      workId: docSnap.id,
      type: data.type || 'Work',
      status: data.status || 'Draft',
      coverArtUrl,
      imageUrl: coverArtUrl,
    } as MediaItem;
  } catch (error) {
    console.error("Error fetching work:", error);
    return null;
  }
}

/**
 * Creates a new release (Album, EP, etc.)
 */
export async function createRelease(
  userId: string,
  input: CreateReleaseInput
): Promise<string> {
  try {
    const releaseId = `release_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const releaseRef = doc(db, 'releases', releaseId);

    const releaseData: Record<string, unknown> = {
      releaseId,
      title: input.title,
      releaseType: input.releaseType,
      artist: {
        docId: userId,
        collection: 'users',
        name: input.artist,
        path: `users/${userId}`,
      } as EntityRef,
      coverArtUrl: input.coverArtUrl || '',
      tracks: [],
      totalDurationMs: 0,
      genre: normalizeGenre(input.genre || ''),
      explicit: input.explicit || false,
      copyrightHolder: input.copyrightHolder || '',
      copyrightYear: input.copyrightYear || new Date().getFullYear(),
      upc: input.upc || '',
      catalogNumber: input.catalogNumber || '',
      distributionStatus: {},
      owner: {
        userId,
      } as ActingAs,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      releaseDate: input.releaseDate ? Timestamp.fromDate(input.releaseDate) : serverTimestamp(),
    };

    await setDoc(releaseRef, releaseData);
    void propagateReleaseCoverById(releaseId);
  return releaseId;
  } catch (error) {
    console.error("Error creating release:", error);
    throw error;
  }
}

/**
 * Create a SINGLE release wrapping one freshly-uploaded track, and link the
 * work back to it. A single uploaded on its own should still appear in the
 * artist's discography / releases — not just as a loose track.
 */
export async function createSingleRelease(
  userId: string,
  opts: {
    workId: string;
    title: string;
    artist: string;
    artistId?: string;
    coverArtUrl?: string;
    images?: string[];
    audioUrl?: string;
    durationSeconds?: number;
    genre?: string;
    explicit?: boolean;
    copyrightHolder?: string;
    copyrightYear?: number;
    releaseDate?: Date;
    status?: string;
  }
): Promise<string> {
  const releaseId = `release_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  await setDoc(doc(db, 'releases', releaseId), {
    releaseId,
    title: opts.title,
    releaseType: 'single',
    artist: { docId: opts.artistId || userId, collection: 'artists', name: opts.artist, path: '' },
    artistName: opts.artist,
    artistId: opts.artistId || '',
    coverArtUrl: opts.coverArtUrl || '',
    images: opts.images || (opts.coverArtUrl ? [opts.coverArtUrl] : []),
    tracks: [{
      workId: opts.workId,
      id: opts.workId,
      title: opts.title,
      trackNumber: 1,
      mp3Url: opts.audioUrl || '',
      ...(opts.durationSeconds ? { durationSeconds: opts.durationSeconds } : {}),
    }],
    genre: normalizeGenre(opts.genre || ''),
    explicit: opts.explicit || false,
    copyrightHolder: opts.copyrightHolder || '',
    copyrightYear: opts.copyrightYear || new Date().getFullYear(),
    owner: { userId },
    createdByUid: userId,
    ownerUid: userId,
    status: opts.status || 'Published',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    releaseDate: opts.releaseDate ? Timestamp.fromDate(opts.releaseDate) : serverTimestamp(),
  });
  // Link the work back to its release so the track page / discography resolve it.
  try { await updateDoc(doc(db, 'works', opts.workId), { releaseId }); } catch { /* non-fatal */ }
  // If the artist is attached to user(s), connect them to this release + track.
  try {
    const { connectArtistToReleaseAndTracks } = await import('./artist-link');
    await connectArtistToReleaseAndTracks({ releaseId, artistId: opts.artistId, workIds: [opts.workId] });
  } catch { /* non-fatal */ }
  void propagateReleaseCoverById(releaseId);
  return releaseId;
}

/**
 * Create a release (single / EP / album / compilation…) from EXISTING works in
 * the vault, and link each work back to it. Lets a creator turn already-uploaded
 * tracks into a release so they surface in the artist's discography.
 */
export async function createReleaseFromWorks(
  userId: string,
  opts: {
    title: string;
    releaseType?: string;             // 'single' | 'ep' | 'album' | 'compilation' | 'mixtape' | 'live album'
    artist: string;
    artistId?: string;
    coverArtUrl?: string;
    images?: string[];
    genre?: string;
    releaseDate?: Date;
    status?: string;
    explicit?: boolean;
    tracks: { workId: string; title: string; audioUrl?: string; durationSeconds?: number; explicit?: boolean }[];
  }
): Promise<string> {
  const releaseId = `release_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const tracks = opts.tracks.map((t, i) => ({
    workId: t.workId,
    id: t.workId,
    title: t.title,
    trackNumber: i + 1,
    mp3Url: t.audioUrl || '',
    explicit: !!t.explicit,
    ...(t.durationSeconds ? { durationSeconds: t.durationSeconds } : {}),
  }));
  // A release is Parental-Advisory if explicitly set OR any of its tracks is
  // explicit (a release can be partly explicit).
  const releaseExplicit = opts.explicit === true || opts.tracks.some(t => t.explicit);
  await setDoc(doc(db, 'releases', releaseId), {
    releaseId,
    title: opts.title,
    releaseType: (opts.releaseType || 'compilation').toLowerCase(),
    artist: { docId: opts.artistId || userId, collection: 'artists', name: opts.artist, path: '' },
    artistName: opts.artist,
    artistId: opts.artistId || '',
    coverArtUrl: opts.coverArtUrl || '',
    images: opts.images || (opts.coverArtUrl ? [opts.coverArtUrl] : []),
    tracks,
    explicit: releaseExplicit,
    genre: normalizeGenre(opts.genre || ''),
    owner: { userId },
    createdByUid: userId,
    ownerUid: userId,
    status: opts.status || 'Published',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    releaseDate: opts.releaseDate ? Timestamp.fromDate(opts.releaseDate) : serverTimestamp(),
  });
  // Link each work back to this release (+ artist, if provided) so the track
  // page / discography resolve it and the artist page lists it.
  await Promise.all(opts.tracks.map(async (t) => {
    try {
      await updateDoc(doc(db, 'works', t.workId), {
        releaseId,
        ...(opts.artistId ? { artistId: opts.artistId, artist: opts.artist, artistName: opts.artist } : {}),
      });
    } catch { /* non-fatal */ }
  }));
  try {
    const { connectArtistToReleaseAndTracks } = await import('./artist-link');
    await connectArtistToReleaseAndTracks({ releaseId, artistId: opts.artistId, workIds: opts.tracks.map(t => t.workId) });
  } catch { /* non-fatal */ }
  void propagateReleaseCoverById(releaseId);
  return releaseId;
}

/**
 * Gets all releases (admin / super admin)
 */
export async function getReleases(): Promise<Release[]> {
  return getReleasesForUser(null);
}

/**
 * Get releases.
 * - uid = null  → return ALL releases (admin)
 * - uid = string → return only releases owned by that user (owner.userId)
 */
export async function getReleasesForUser(uid: string | null): Promise<Release[]> {
  try {
    const releasesCollection = collection(db, 'releases');
    const releasesQuery = uid
      ? query(releasesCollection, where('owner.userId', '==', uid), orderBy('createdAt', 'desc'))
      : query(releasesCollection, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(releasesQuery);
    return querySnapshot.docs.map(d => ({
      ...d.data(),
      releaseId: d.id,
    })) as Release[];
  } catch (error) {
    console.error('Error fetching releases for user:', error);
    // Fallback: no orderBy (avoids missing-index errors for new users)
    try {
      const releasesCollection = collection(db, 'releases');
      const releasesQuery = uid
        ? query(releasesCollection, where('owner.userId', '==', uid))
        : query(releasesCollection);
      const querySnapshot = await getDocs(releasesQuery);
      return querySnapshot.docs.map(d => ({
        ...d.data(),
        releaseId: d.id,
      })) as Release[];
    } catch (err2) {
      console.error('Error fetching releases fallback:', err2);
      return [];
    }
  }
}

// ─── Contributor matching ──────────────────────────────────────────────────

export interface ContributorMatch {
  id: string;
  title: string;
  type: 'work' | 'release';
  artist?: string;
  coverArtUrl?: string;
  roles: string[];
}

/**
 * Find all works and releases where the given email appears in contributorEmails[].
 * The `contributorEmails` array is a flat, indexed copy of contributors[].email
 * written by createWork / the bulk-upload pipeline.
 */
export async function checkExistingContributions(
  email: string,
): Promise<ContributorMatch[]> {
  if (!email) return [];
  const norm = email.toLowerCase().trim();

  try {
    const [worksSnap, releasesSnap] = await Promise.all([
      getDocs(query(collection(db, 'works'), where('contributorEmails', 'array-contains', norm))),
      getDocs(query(collection(db, 'releases'), where('contributorEmails', 'array-contains', norm))),
    ]);

    const matches: ContributorMatch[] = [];

    worksSnap.forEach(d => {
      const data = d.data() as any;
      // Find the contributor entry to get their roles
      const contrib = (data.contributors || []).find(
        (c: any) => (c.email || '').toLowerCase() === norm,
      );
      matches.push({
        id: d.id,
        title: data.title || 'Untitled',
        type: 'work',
        artist: data.artist || data.artistName || '',
        coverArtUrl: data.coverArtUrl || '',
        roles: contrib?.roles || (contrib?.role ? [contrib.role] : []),
      });
    });

    releasesSnap.forEach(d => {
      const data = d.data() as any;
      const contrib = (data.contributors || []).find(
        (c: any) => (c.email || '').toLowerCase() === norm,
      );
      matches.push({
        id: d.id,
        title: data.title || 'Untitled',
        type: 'release',
        artist: data.artist || data.artistName || '',
        coverArtUrl: data.coverArtUrl || '',
        roles: contrib?.roles || (contrib?.role ? [contrib.role] : []),
      });
    });

    return matches;
  } catch (err) {
    console.error('checkExistingContributions error:', err);
    return [];
  }
}

/**
 * Stamps userId onto any work/release where this user is listed as a contributor.
 * Called after the user completes onboarding so future queries can filter by userId.
 */
export async function linkUserToContributions(
  userId: string,
  email: string,
): Promise<void> {
  if (!userId || !email) return;
  const norm = email.toLowerCase().trim();

  try {
    const [worksSnap, releasesSnap] = await Promise.all([
      getDocs(query(collection(db, 'works'), where('contributorEmails', 'array-contains', norm))),
      getDocs(query(collection(db, 'releases'), where('contributorEmails', 'array-contains', norm))),
    ]);

    const allDocs = [
      ...worksSnap.docs.map(d => ({ ref: d.ref, data: d.data() as any })),
      ...releasesSnap.docs.map(d => ({ ref: d.ref, data: d.data() as any })),
    ];

    await Promise.all(
      allDocs.map(({ ref, data }) => {
        const updatedContributors = (data.contributors || []).map((c: any) =>
          (c.email || '').toLowerCase() === norm ? { ...c, userId } : c,
        );
        return updateDoc(ref, { contributors: updatedContributors });
      }),
    );
  } catch (err) {
    console.error('linkUserToContributions error:', err);
  }
}
